import { fonts } from '@pixee/design-system'
import { useLayoutEffect, useRef, useState } from 'react'
import { CountBadge } from '../badges/count-badge.tsx'
import * as styles from './repository-names.css.ts'

export type RepositoryNamesProps = {
  /** The member names, in the order the API lists them. */
  names: string[]
  /** The project's full member count; the names may be a sample of it. */
  total: number
}

const GAP = 8
// A canvas measures a hair under what the browser lays out, so keep a little back.
const SAFETY = 12

let measuringContext: CanvasRenderingContext2D | null | undefined

const measure = (text: string, font: string) => {
  if (measuringContext === undefined) {
    measuringContext = document.createElement('canvas').getContext('2d')
  }
  if (!measuringContext) {
    return text.length * 7
  }
  measuringContext.font = font
  return measuringContext.measureText(text).width
}

/**
 * A count badge followed by as many member names as fit on one line, then "+N more". Fitting is
 * measured, not truncated with an ellipsis: a cut-off name tells the reader nothing, a count does.
 */
export const RepositoryNames: React.FC<RepositoryNamesProps> = ({ names, total }) => {
  const listRef = useRef<HTMLSpanElement>(null)
  const badgeRef = useRef<HTMLSpanElement>(null)
  const [shown, setShown] = useState(names.length)

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || names.length === 0) {
      return
    }

    const fit = () => {
      const font = getComputedStyle(list).getPropertyValue('font') || fonts.small
      const available = list.clientWidth - (badgeRef.current?.offsetWidth ?? 0) - GAP - SAFETY
      let count = names.length
      while (count > 1) {
        const hidden = total - count
        const used =
          measure(names.slice(0, count).join(', '), font) + (hidden > 0 ? GAP + measure(`+${hidden} more`, font) : 0)
        if (used <= available) {
          break
        }
        count -= 1
      }
      setShown(count)
    }

    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(list)
    // The first measurement can land before the web font arrives; re-fit once it has. jsdom has no
    // FontFaceSet, so the field is treated as optional.
    const fontFaces: FontFaceSet | undefined = document.fonts
    void fontFaces?.ready.then(fit)
    return () => observer.disconnect()
  }, [names, total])

  const hidden = total - Math.min(shown, names.length)

  return (
    <span ref={listRef} className={styles.list}>
      <span ref={badgeRef}>
        <CountBadge count={total} variant={total === 0 ? 'muted' : 'default'} />
      </span>
      {names.length === 0 ? (
        <span className={styles.none}>None yet</span>
      ) : (
        <>
          <span className={styles.names}>{names.slice(0, shown).join(', ')}</span>
          {hidden > 0 && <span className={styles.more}>+{hidden} more</span>}
        </>
      )}
    </span>
  )
}
